import { useCallback, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { LEDGER_OPERATION_KINDS, LEDGER_TYPE_BY_KIND } from "../constants/paymentsLedger.constants";
import { PaymentsLedgerService } from "../services/paymentsLedger.service";
import type { LedgerOperationKind } from "../types/paymentsLedger.types";
import { ledgerAccessLatch } from "../utils/ledgerAccessLatch";

/**
 * Outcome of resolving a pasted id.
 *
 * `not-found` is deliberately NOT "this id does not exist": an operation that has posted nothing yet has no
 * entries either (a payin whose QR was minted and never paid). Both answer `200 []` and the UI must not claim
 * more than it knows.
 */
export type OperationKindLookup =
  | { status: "resolved"; kind: LedgerOperationKind }
  | { status: "not-found" }
  | { status: "failed" };

/**
 * Resolves which kind of operation an id belongs to, by asking the journal itself.
 *
 * Nothing in a uuid says whether it is a payout, a payin or a swap, and the three detail endpoints cannot
 * answer it either — the swap one needs an `organization_id` a pasted id does not carry (`requireOrg` → 400).
 * But the ledger endpoint can: `GET /internal/payments/ledger/entries` is a plain lookup by `(operation_type,
 * operation_id)` with **no tenancy filter**, and it is already the endpoint behind this page. Probing the
 * three primary types in parallel makes the journal name the kind — an authoritative answer, not a guess at
 * which tab the user had open.
 *
 * `REVERSE` is deliberately never probed: `LedgerService.reverse` stamps the mirror entry with the ORIGINAL
 * `operation_id`, so every reversed operation would match it and turn ambiguous.
 *
 * One request per kind, on an explicit user action only — never on render, and no polling.
 */
export interface UseOperationKindLookupResult {
  isResolving: boolean;
  resolve: (operationId: string) => Promise<OperationKindLookup>;
}

export const useOperationKindLookup = (): UseOperationKindLookupResult => {
  const { user } = useAuth();
  const deniedKey = user?.id ?? "anon";
  const [isResolving, setIsResolving] = useState(false);

  const resolve = useCallback(
    async (operationId: string): Promise<OperationKindLookup> => {
      if (ledgerAccessLatch.isDenied(deniedKey)) return { status: "failed" };
      setIsResolving(true);
      try {
        const probes = await Promise.all(
          LEDGER_OPERATION_KINDS.map(async (kind) => {
            const response = await PaymentsLedgerService.listByOperation(
              LEDGER_TYPE_BY_KIND[kind],
              operationId,
            );
            return { kind, hasEntries: response.data.length > 0 };
          }),
        );

        // Distinct uuids cannot collide across types; if one ever did, registry order decides rather than
        // throwing away a real journal.
        const match = probes.find((probe) => probe.hasEntries);
        return match ? { status: "resolved", kind: match.kind } : { status: "not-found" };
      } catch (err: unknown) {
        // Same permission as every other read here, so a denial latches the whole surface — the page then
        // renders NoAccessView instead of leaving this message as the only clue.
        if (HttpApiService.isAccessDeniedError(err)) ledgerAccessLatch.latch(deniedKey);
        // Never navigate to an operation the lookup could not verify.
        return { status: "failed" };
      } finally {
        setIsResolving(false);
      }
    },
    [deniedKey],
  );

  return { isResolving, resolve };
};
